/**
 * CommissionPreview.js
 * Component for previewing and calculating commission distribution
 * Task 7 - Module 5 UI
 */

import React, { useState, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Chart } from 'primereact/chart';
import {
    collaborationService,
    getRoleConfig,
    formatCurrency,
    formatPercentage,
    COMMISSION_MODELS
} from '../../services/collaborationService';

const CommissionPreview = ({
    collaborationId,
    members = [],
    commissionModel,
    isOwner = false,
    currentUserId,
    onComplete
}) => {
    const toast = useRef(null);

    // Form state
    const [loanAmount, setLoanAmount] = useState(null);
    const [totalCommission, setTotalCommission] = useState(null);

    // Preview state
    const [previewData, setPreviewData] = useState(null);
    const [calculating, setCalculating] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    // Warnings
    const [warnings, setWarnings] = useState([]);

    // Calculate preview
    const handleCalculate = async () => {
        if (!loanAmount || loanAmount <= 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Amount Required',
                detail: 'Please enter the loan amount disbursed',
                life: 3000
            });
            return;
        }

        if (!totalCommission || totalCommission <= 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Commission Required',
                detail: 'Please enter the total commission earned',
                life: 3000
            });
            return;
        }

        setCalculating(true);
        setWarnings([]);

        try {
            const response = await collaborationService.previewCommission(collaborationId, {
                loan_amount_disbursed: loanAmount,
                total_commission: totalCommission
            });

            if (response.success) {
                setPreviewData(response.data);

                // Check for warnings
                const newWarnings = [];
                if (response.data.has_pending_milestones) {
                    newWarnings.push('Some members have pending milestones');
                }
                if (response.data.total_percentage > 100) {
                    newWarnings.push('Total percentage exceeds 100%');
                }
                if (response.data.insufficient_balance) {
                    newWarnings.push('Insufficient wallet balance for distribution');
                }
                setWarnings(newWarnings);
            }
        } catch (error) {
            console.error('Error calculating preview:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Calculation Failed',
                detail: error.response?.data?.message || 'Failed to calculate commission preview',
                life: 4000
            });
        } finally {
            setCalculating(false);
        }
    };

    // Handle proceed to complete
    const handleProceedComplete = () => {
        if (!confirmed) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Confirmation Required',
                detail: 'Please confirm the loan has been disbursed',
                life: 3000
            });
            return;
        }

        if (onComplete) {
            onComplete({
                loan_amount_disbursed: loanAmount,
                total_commission: totalCommission,
                distribution: previewData?.distribution || []
            });
        }
    };

    // Generate chart data for percentage split
    const generatePieChartData = () => {
        if (!previewData?.distribution) return null;

        const colors = ['#8B5CF6', '#3B82F6', '#14B8A6', '#F59E0B', '#EF4444', '#EC4899'];

        return {
            labels: previewData.distribution.map(d => d.member_name),
            datasets: [{
                data: previewData.distribution.map(d => d.percentage || d.amount),
                backgroundColor: previewData.distribution.map((_, i) => colors[i % colors.length]),
                hoverBackgroundColor: previewData.distribution.map((_, i) => colors[i % colors.length])
            }]
        };
    };

    // Chart options
    const chartOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true
                }
            }
        },
        maintainAspectRatio: false
    };

    // Table templates
    const memberTemplate = (rowData) => {
        const roleConfig = getRoleConfig(rowData.role);
        const isCurrentUser = rowData.user_id === currentUserId;

        return (
            <div className="flex align-items-center gap-2">
                <Avatar
                    image={rowData.avatar}
                    label={rowData.member_name?.charAt(0) || 'M'}
                    size="normal"
                    shape="circle"
                    style={{ backgroundColor: roleConfig.color, color: '#fff' }}
                />
                <div>
                    <div className={`font-medium ${isCurrentUser ? 'text-primary' : ''}`}>
                        {rowData.member_name}
                        {isCurrentUser && <span className="text-500 text-sm ml-1">(You)</span>}
                    </div>
                </div>
            </div>
        );
    };

    const roleTemplate = (rowData) => {
        const roleConfig = getRoleConfig(rowData.role);
        return <Tag value={roleConfig.label} style={{ backgroundColor: roleConfig.color }} />;
    };

    const basisTemplate = (rowData) => {
        if (commissionModel === COMMISSION_MODELS.PERCENTAGE_SPLIT) {
            return <span>{formatPercentage(rowData.percentage)}</span>;
        }
        if (commissionModel === COMMISSION_MODELS.FIXED_AMOUNT) {
            return <Tag value="Fixed" severity="info" />;
        }
        if (commissionModel === COMMISSION_MODELS.MILESTONE_BASED) {
            return <span>{rowData.milestones_count || 0} milestones</span>;
        }
        return '-';
    };

    const amountTemplate = (rowData) => {
        const isCurrentUser = rowData.user_id === currentUserId;
        return (
            <span className={`font-semibold ${isCurrentUser ? 'text-primary text-lg' : ''}`}>
                {formatCurrency(rowData.amount)}
            </span>
        );
    };

    const statusTemplate = (rowData) => {
        if (rowData.status === 'pending_milestone') {
            return <Tag value="Pending Milestone" severity="warning" />;
        }
        return <Tag value="Will Receive" severity="success" />;
    };

    // Render preview form
    const renderPreviewForm = () => (
        <Card className="mb-4">
            <div className="flex align-items-center gap-2 mb-4">
                <i className="pi pi-calculator text-primary" style={{ fontSize: '1.25rem' }} />
                <span className="font-semibold text-lg">Commission Calculator</span>
            </div>

            <div className="grid">
                <div className="col-12 md:col-6">
                    <div className="field">
                        <label htmlFor="loan-amount" className="font-medium mb-2 block">
                            Loan Amount Disbursed <span className="text-red-500">*</span>
                        </label>
                        <InputNumber
                            id="loan-amount"
                            value={loanAmount}
                            onValueChange={(e) => setLoanAmount(e.value)}
                            mode="currency"
                            currency="INR"
                            locale="en-IN"
                            placeholder="Enter final disbursed amount"
                            className="w-full"
                            min={0}
                        />
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field">
                        <label htmlFor="total-commission" className="font-medium mb-2 block">
                            Total Commission Earned <span className="text-red-500">*</span>
                        </label>
                        <InputNumber
                            id="total-commission"
                            value={totalCommission}
                            onValueChange={(e) => setTotalCommission(e.value)}
                            mode="currency"
                            currency="INR"
                            locale="en-IN"
                            placeholder="Enter total commission"
                            className="w-full"
                            min={0}
                        />
                    </div>
                </div>
            </div>

            <Button
                label="Calculate Preview"
                icon="pi pi-calculator"
                onClick={handleCalculate}
                loading={calculating}
                disabled={!loanAmount || !totalCommission}
                className="mt-2"
            />
        </Card>
    );

    // Render summary cards
    const renderSummaryCards = () => {
        if (!previewData) return null;

        const yourShare = previewData.distribution?.find(d => d.user_id === currentUserId);

        return (
            <div className="grid mb-4">
                <div className="col-12 md:col-4">
                    <div className="surface-card border-round p-3 h-full">
                        <div className="text-500 text-sm mb-1">Total Commission</div>
                        <div className="text-2xl font-bold text-900">
                            {formatCurrency(previewData.total_commission || totalCommission)}
                        </div>
                    </div>
                </div>
                <div className="col-12 md:col-4">
                    <div className="surface-card border-round p-3 h-full">
                        <div className="text-500 text-sm mb-1">Members</div>
                        <div className="text-2xl font-bold text-900">
                            {previewData.distribution?.length || members.length}
                        </div>
                    </div>
                </div>
                <div className="col-12 md:col-4">
                    <div className="surface-card border-round p-3 h-full border-left-3 border-primary">
                        <div className="text-500 text-sm mb-1">Your Share</div>
                        <div className="text-2xl font-bold text-primary">
                            {formatCurrency(yourShare?.amount || 0)}
                        </div>
                        {commissionModel === COMMISSION_MODELS.PERCENTAGE_SPLIT && yourShare && (
                            <div className="text-sm text-500">
                                {formatPercentage(yourShare.percentage)} of total
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render distribution table
    const renderDistributionTable = () => {
        if (!previewData?.distribution) return null;

        const totalDistributed = previewData.distribution.reduce((sum, d) => sum + (d.amount || 0), 0);
        const remaining = (previewData.total_commission || totalCommission) - totalDistributed;

        return (
            <div className="mb-4">
                <div className="font-semibold mb-3">Distribution Breakdown</div>
                <DataTable
                    value={previewData.distribution}
                    className="p-datatable-sm"
                    rowClassName={(data) => data.user_id === currentUserId ? 'bg-primary-50' : ''}
                >
                    <Column header="Member" body={memberTemplate} />
                    <Column header="Role" body={roleTemplate} style={{ width: '120px' }} />
                    <Column header="Basis" body={basisTemplate} style={{ width: '120px' }} />
                    <Column header="Amount" body={amountTemplate} style={{ width: '150px' }} />
                    <Column header="Status" body={statusTemplate} style={{ width: '140px' }} />
                </DataTable>

                {/* Footer totals */}
                <div className="flex justify-content-between p-3 surface-100 border-round-bottom">
                    <span className="font-semibold">Total Distributed</span>
                    <span className="font-semibold">{formatCurrency(totalDistributed)}</span>
                </div>
                {remaining > 0 && (
                    <div className="flex justify-content-between p-3 surface-50">
                        <span className="text-500">Remaining</span>
                        <span className="text-500">{formatCurrency(remaining)}</span>
                    </div>
                )}
            </div>
        );
    };

    // Render visual breakdown
    const renderVisualBreakdown = () => {
        if (!previewData?.distribution) return null;

        if (commissionModel === COMMISSION_MODELS.PERCENTAGE_SPLIT) {
            const chartData = generatePieChartData();
            if (!chartData) return null;

            return (
                <div className="mb-4">
                    <div className="font-semibold mb-3">Distribution Chart</div>
                    <div style={{ height: '250px' }}>
                        <Chart type="doughnut" data={chartData} options={chartOptions} />
                    </div>
                </div>
            );
        }

        if (commissionModel === COMMISSION_MODELS.FIXED_AMOUNT) {
            const total = previewData.total_commission || totalCommission;

            return (
                <div className="mb-4">
                    <div className="font-semibold mb-3">Distribution Overview</div>
                    {previewData.distribution.map((member, index) => {
                        const percentage = total > 0 ? (member.amount / total) * 100 : 0;
                        const colors = ['#8B5CF6', '#3B82F6', '#14B8A6', '#F59E0B', '#EF4444'];

                        return (
                            <div key={member.user_id || index} className="mb-3">
                                <div className="flex justify-content-between mb-1">
                                    <span className="text-700">{member.member_name}</span>
                                    <span className="font-semibold">{formatCurrency(member.amount)}</span>
                                </div>
                                <ProgressBar
                                    value={percentage}
                                    showValue={false}
                                    style={{ height: '8px' }}
                                    color={colors[index % colors.length]}
                                 aria-label="Progress" />
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (commissionModel === COMMISSION_MODELS.MILESTONE_BASED) {
            return (
                <div className="mb-4">
                    <div className="font-semibold mb-3">Milestone Status</div>
                    {previewData.milestones?.map((milestone, index) => (
                        <div
                            key={milestone.id || index}
                            className={`flex align-items-center gap-3 p-3 mb-2 border-round ${
                                milestone.completed ? 'surface-100' : 'surface-50'
                            }`}
                        >
                            <i
                                className={`pi ${milestone.completed ? 'pi-check-circle text-green-500' : 'pi-circle text-gray-400'}`}
                                style={{ fontSize: '1.25rem' }}
                            />
                            <div className="flex-grow-1">
                                <div className="font-medium">{milestone.name}</div>
                                <div className="text-sm text-500">
                                    Assigned to: {milestone.assigned_to || 'Unassigned'}
                                </div>
                            </div>
                            <div className="font-semibold">
                                {formatCurrency(milestone.amount)}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        return null;
    };

    // Render warnings
    const renderWarnings = () => {
        if (warnings.length === 0) return null;

        return (
            <div className="mb-4">
                {warnings.map((warning, index) => (
                    <Message
                        key={index}
                        severity="warn"
                        text={warning}
                        className="w-full mb-2"
                    />
                ))}
            </div>
        );
    };

    // Render action section
    const renderActionSection = () => {
        if (!previewData || !isOwner) return null;

        return (
            <div className="surface-100 border-round p-4">
                <div className="field-checkbox mb-3">
                    <Checkbox
                        inputId="confirm-disbursed"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.checked)}
                        className="mr-2"
                    />
                    <label htmlFor="confirm-disbursed" className="cursor-pointer">
                        I confirm the loan has been disbursed and the amounts are correct
                    </label>
                </div>

                <div className="flex gap-2">
                    <Button
                        label="Proceed to Complete"
                        icon="pi pi-check-circle"
                        onClick={handleProceedComplete}
                        disabled={!confirmed || warnings.length > 0}
                        className="flex-grow-1"
                    />
                    <Button
                        label="Recalculate"
                        icon="pi pi-refresh"
                        className="p-button-secondary"
                        onClick={handleCalculate}
                        loading={calculating}
                    />
                </div>
            </div>
        );
    };

    // Render loading skeleton
    const renderSkeleton = () => (
        <div>
            <div className="grid mb-4">
                {[1, 2, 3].map(i => (
                    <div key={i} className="col-12 md:col-4">
                        <Skeleton height="80px" className="border-round" />
                    </div>
                ))}
            </div>
            <Skeleton height="200px" className="border-round mb-4" />
            <Skeleton height="150px" className="border-round" />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />

            {/* Preview Form */}
            {renderPreviewForm()}

            {/* Loading State */}
            {calculating && renderSkeleton()}

            {/* Preview Results */}
            {!calculating && previewData && (
                <>
                    {/* Summary Cards */}
                    {renderSummaryCards()}

                    {/* Warnings */}
                    {renderWarnings()}

                    {/* Distribution Table */}
                    {renderDistributionTable()}

                    {/* Visual Breakdown */}
                    {renderVisualBreakdown()}

                    <Divider />

                    {/* Action Section */}
                    {renderActionSection()}
                </>
            )}

            {/* Empty state when no preview yet */}
            {!calculating && !previewData && (
                <div className="text-center p-5 surface-100 border-round">
                    <i className="pi pi-chart-pie text-primary mb-3" style={{ fontSize: '3rem' }} />
                    <div className="text-xl font-semibold mb-2">Preview Commission Distribution</div>
                    <div className="text-500 mb-4">
                        Enter the loan disbursement amount and total commission to see how it will be distributed among team members.
                    </div>
                </div>
            )}
        </>
    );
};

export default CommissionPreview;
